import React, { useState } from 'react';
import { LuX, LuCreditCard } from 'react-icons/lu';
import { FaUser } from 'react-icons/fa';
import validator from 'validator';
import { BiBuilding } from 'react-icons/bi';
import toast from 'react-hot-toast';
import bankDetailsService from '../../../services/api/bankdetail.service';

const AddPayoutDetailsCanvas = ({ fetchPayoutDetails, showPayoutCanvas, setShowPayoutCanvas, }: { fetchPayoutDetails: () => void; showPayoutCanvas: boolean; setShowPayoutCanvas: React.Dispatch<React.SetStateAction<boolean>>; }) => {
    const [bankDetails, setBankDetails] = useState({
        accountNumber: '',
        ifscCode: '',
        accountHolderName: '',
        bankName: '',
    });
    const [bankErrors, setBankErrors] = useState({
        accountNumber: '',
        ifscCode: '',
        accountHolderName: '',
        bankName: '',
    });

    const validateBankName = (name: string): boolean => {
        // Allow letters, spaces, and dots, minimum 3 characters
        const nameRegex = /^[A-Za-z\s.]{3,50}$/;

        if (!nameRegex.test(name)) {
            setBankErrors(prev => ({
                ...prev,
                bankName: 'Bank name should be 3-50 characters long and contain only letters, spaces, and dots'
            }));
            return false;
        }

        setBankErrors(prev => ({
            ...prev,
            bankName: ''
        }));
        return true;
    };

    const validateIFSC = (ifsc: string): boolean => {
        const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;

        if (!ifscRegex.test(ifsc)) {
            setBankErrors(prev => ({
                ...prev,
                ifscCode: 'Invalid IFSC format. Example: SBIN0123456'
            }));
            return false;
        }

        setBankErrors(prev => ({
            ...prev,
            ifscCode: ''
        }));
        return true;
    };

    const validateAccountHolderName = (name: string): boolean => {
        // Allow letters, spaces, and dots, minimum 3 characters
        const nameRegex = /^[A-Za-z\s.]{3,50}$/;

        if (!nameRegex.test(name)) {
            setBankErrors(prev => ({
                ...prev,
                accountHolderName: 'Name should be 3-50 characters long and contain only letters, spaces, and dots'
            }));
            return false;
        }

        setBankErrors(prev => ({
            ...prev,
            accountHolderName: ''
        }));
        return true;
    };

    const validateBankDetails = (): boolean => {
        let isValid = true;
        const newErrors = { ...bankErrors };

        if (!validator.isLength(bankDetails.accountNumber, { min: 9, max: 18 })) {
            newErrors.accountNumber = 'Bank account numbers can range from 9 to 18 digits';
            isValid = false;
        } else if (!/^\d+$/.test(bankDetails.accountNumber)) {
            newErrors.accountNumber = 'Account number must contain only digits';
            isValid = false;
        } else {
            newErrors.accountNumber = '';
        }

        if (!validateIFSC(bankDetails.ifscCode)) {
            isValid = false;
        }

        if (!validateAccountHolderName(bankDetails.accountHolderName)) {
            isValid = false;
        }

        setBankErrors(newErrors);
        return isValid;
    };

    const handleIFSCChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toUpperCase();
        setBankDetails({
            ...bankDetails,
            ifscCode: value
        });
        if (value) validateIFSC(value);
    };

    const handleBankNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setBankDetails({
            ...bankDetails,
            bankName: value
        });
        if (value) validateBankName(value);
    };

    const handleAccountHolderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setBankDetails({
            ...bankDetails,
            accountHolderName: value
        });
        if (value) validateAccountHolderName(value);
    };

    const handleBankDetailsSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (validateBankDetails()) {
            try {
                const response = await bankDetailsService.addBankDetails({
                    account_number: bankDetails.accountNumber,
                    ifsc_code: bankDetails.ifscCode,
                    account_holder_name: bankDetails.accountHolderName,
                    bank_name: bankDetails.bankName
                });
                toast.success((response as any)?.message);
                setShowPayoutCanvas(false);
                fetchPayoutDetails();
            } catch (error) {
                toast.error((error as any)?.response?.data?.message || 'Something went wrong');
                console.error('Error submitting details:', error);
            }
        }
    };

    function onClose() {
        setShowPayoutCanvas(false);
        setBankDetails({
            accountNumber: '',
            ifscCode: '',
            accountHolderName: '',
            bankName: ''
        });
        setBankErrors({
            accountNumber: '',
            ifscCode: '',
            accountHolderName: '',
            bankName: ''
        })
    }

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 z-50 ${showPayoutCanvas ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                onClick={() => onClose()}
            />

            {/* Canvas */}
            <div
                className={`fixed right-0 top-0 z-50 h-screen w-full sm:max-w-md bg-white shadow-xl transform transition-transform duration-300 ${showPayoutCanvas ? 'translate-x-0' : 'translate-x-full'
                    }`}
            >
                <div className="flex flex-col h-full">
                    {/* Header */}
                    <div className="p-6 border-b">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-semibold">
                                Add Bank Details
                            </h2>
                            <button
                                onClick={() => onClose()}
                                className="p-2 hover:bg-gray-100 rounded-lg"
                            >
                                <LuX className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        <form onSubmit={handleBankDetailsSubmit} className="space-y-6">
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Bank Name*
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                                        <BiBuilding className="text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        value={bankDetails.bankName}
                                        onChange={handleBankNameChange}
                                        className={`pl-10 w-full p-3 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-primary-100 ${bankErrors.bankName ? 'border-red-500' : ''}`}
                                        placeholder="Enter bank name"
                                        required
                                    />
                                </div>
                                {bankErrors.bankName && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {bankErrors.bankName}
                                    </p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Account Holder Name*
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                                        <FaUser className="text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        value={bankDetails.accountHolderName}
                                        onChange={handleAccountHolderNameChange}
                                        className={`pl-10 w-full p-3 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-primary-100 ${bankErrors.accountHolderName ? 'border-red-500' : ''
                                            }`}
                                        placeholder="Enter account holder name"
                                        required
                                    />
                                </div>
                                {bankErrors.accountHolderName && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {bankErrors.accountHolderName}
                                    </p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Account Number*
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                                        <LuCreditCard className="text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        value={bankDetails.accountNumber}
                                        onChange={(e) =>
                                            setBankDetails({
                                                ...bankDetails,
                                                accountNumber: e.target.value.replace(/\D/g, '')
                                            })
                                        }
                                        className={`pl-10 w-full p-3 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-primary-100 ${bankErrors.accountNumber ? 'border-red-500' : ''
                                            }`}
                                        placeholder="Enter account number"
                                        required
                                    />
                                </div>
                                {bankErrors.accountNumber && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {bankErrors.accountNumber}
                                    </p>
                                )}
                            </div>

                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    IFSC Code*
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                                        <BiBuilding className="text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        value={bankDetails.ifscCode}
                                        onChange={handleIFSCChange}
                                        className={`pl-10 w-full p-3 border rounded-lg bg-gray-50 focus:ring-2 focus:ring-primary-100 ${bankErrors.ifscCode ? 'border-red-500' : ''
                                            }`}
                                        placeholder="Example: SBIN0123456"
                                        maxLength={11}
                                        required
                                    />
                                </div>
                                {bankErrors.ifscCode && (
                                    <p className="text-red-500 text-sm mt-1">
                                        {bankErrors.ifscCode}
                                    </p>
                                )}
                            </div>

                            <button
                                type="submit"
                                className="w-full bg-primary-600 text-white py-3 rounded-lg hover:bg-primary-700 transition-colors"
                            >
                                Submit Bank Details
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddPayoutDetailsCanvas;